import React, { useState, useEffect } from 'react';
import {
  XMarkIcon,
  MapPinIcon,
  CalendarIcon,
  UserIcon,
  ExclamationTriangleIcon,
  DocumentIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  ClockIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { getBaseUrl } from '../utils/config';

interface IncidentViewModalProps {
  incident: any;
  onClose: () => void;
}

const IncidentViewModal: React.FC<IncidentViewModalProps> = ({ incident, onClose }) => {
  const { token } = useAuth();
  const [previewFile, setPreviewFile] = useState<any>(null);
  const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  // Cleanup blob URL on component unmount
  useEffect(() => {
    return () => {
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
      }
    };
  }, [previewBlobUrl]);

  if (!incident) return null;

  const downloadFile = async (file: any) => {
    try {
      const authToken = token || localStorage.getItem('token');
      
      const response = await fetch(`${getBaseUrl()}${file.url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error('Failed to download file');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = file.originalname || file.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download file');
    }
  };

  const openFile = (file: any) => {
    const authToken = token || localStorage.getItem('token');
    const fileUrl = `${getBaseUrl()}${file.url}?token=${authToken}`;
    window.open(fileUrl, '_blank');
  };

  const previewFileInCanvas = async (file: any) => {
    console.log('Preview file clicked:', file);
    setLoadingPreview(true);
    
    try {
      // Clean up previous blob URL
      if (previewBlobUrl) {
        URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl(null);
      }

      const authToken = token || localStorage.getItem('token');
      const response = await fetch(`${getBaseUrl()}${file.url}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${authToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);
      
      setPreviewFile(file);
      setPreviewBlobUrl(blobUrl);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to load file preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  const closePreview = () => {
    if (previewBlobUrl) {
      URL.revokeObjectURL(previewBlobUrl);
      setPreviewBlobUrl(null);
    }
    setPreviewFile(null);
  };

  const renderFilePreview = () => {
    if (!previewFile) {
      console.log('No preview file available');
      return null;
    }

    if (loadingPreview) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
          <p className="text-gray-600">Loading preview...</p>
        </div>
      );
    }

    if (!previewBlobUrl) {
      return (
        <div className="flex flex-col items-center justify-center space-y-4">
          <p className="text-red-600">Failed to load preview</p>
          <button
            onClick={() => previewFileInCanvas(previewFile)}
            className="btn btn-secondary"
          >
            Retry
          </button>
        </div>
      );
    }

    console.log('Rendering preview for:', previewFile.mimetype, previewBlobUrl);

    if (isImageFile(previewFile.mimetype)) {
      return (
        <img
          src={previewBlobUrl}
          alt={previewFile.originalname}
          className="max-w-full max-h-full object-contain"
          onError={(e) => {
            toast.error('Failed to load image preview');
            closePreview();
          }}
        />
      );
    }

    if (isPdfFile(previewFile.mimetype)) {
      return (
        <iframe
          src={previewBlobUrl}
          className="w-full h-full border-0"
          title={previewFile.originalname}
          onError={() => {
            toast.error('Failed to load PDF preview');
            closePreview();
          }}
        />
      );
    }

    if (isVideoFile(previewFile.mimetype)) {
      return (
        <video
          src={previewBlobUrl}
          controls
          className="max-w-full max-h-full"
          onError={() => {
            toast.error('Failed to load video preview');
            closePreview();
          }}
        >
          Your browser does not support the video tag.
        </video>
      );
    }

    if (isAudioFile(previewFile.mimetype)) {
      return (
        <div className="flex flex-col items-center justify-center w-full space-y-4">
          <div className="p-6 bg-blue-100 rounded-full">
            <svg className="w-12 h-12 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
            </svg>
          </div>
          <audio
            src={previewBlobUrl}
            controls
            className="w-full"
            onError={() => {
              toast.error('Failed to load audio preview');
              closePreview();
            }}
          >
            Your browser does not support the audio tag.
          </audio>
        </div>
      );
    }

    return (
      <div className="flex flex-col items-center justify-center h-full space-y-4">
        <DocumentIcon className="w-16 h-16 text-gray-400" />
        <p className="text-gray-600">Preview not available for this file type</p>
        <button
          onClick={() => downloadFile(previewFile)}
          className="btn btn-primary"
        >
          Download File
        </button>
      </div>
    );
  };

  const getPriorityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'reported': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'under_review': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'resolved': return 'bg-green-100 text-green-800 border-green-200';
      case 'closed': return 'bg-gray-100 text-gray-800 border-gray-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const isImageFile = (mimetype: string) => {
    return mimetype?.startsWith('image/');
  };

  const isPdfFile = (mimetype: string) => {
    return mimetype === 'application/pdf';
  };

  const isVideoFile = (mimetype: string) => {
    return mimetype?.startsWith('video/');
  };

  const isAudioFile = (mimetype: string) => {
    return mimetype?.startsWith('audio/');
  };

  const getFileTypeIcon = (mimetype: string) => {
    if (isImageFile(mimetype)) return { icon: EyeIcon, color: 'text-green-600', bg: 'bg-green-100' };
    if (isPdfFile(mimetype)) return { icon: DocumentIcon, color: 'text-red-600', bg: 'bg-red-100' };
    if (isVideoFile(mimetype)) return { icon: EyeIcon, color: 'text-purple-600', bg: 'bg-purple-100' };
    if (isAudioFile(mimetype)) return { icon: EyeIcon, color: 'text-blue-600', bg: 'bg-blue-100' };
    return { icon: DocumentIcon, color: 'text-blue-600', bg: 'bg-blue-100' };
  };

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black bg-opacity-50" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative w-full h-full flex">
        <div className="bg-white w-full h-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center space-x-4">
              <div className="p-2 bg-red-100 rounded-lg">
                <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{incident.title}</h1>
                <p className="text-sm text-gray-500">Incident ID: {incident.incident_id}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-hidden flex">
            {/* Left Panel - Incident Details */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8">
              {/* Status and Priority Badges */}
              <div className="flex items-center space-x-4">
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getPriorityColor(incident.severity)}`}>
                  <ExclamationTriangleIcon className="w-4 h-4 mr-1" />
                  {incident.severity?.charAt(0)?.toUpperCase() + incident.severity?.slice(1)} Priority
                </span>
                <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${getStatusColor(incident.status)}`}>
                  <ClockIcon className="w-4 h-4 mr-1" />
                  {incident.status?.replace('_', ' ')?.charAt(0)?.toUpperCase() + incident.status?.replace('_', ' ')?.slice(1)}
                </span>
              </div>

              {/* Main Content Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-6">
                  {/* Incident Details */}
                  <div className="bg-gray-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">Incident Details</h3>
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Incident Type</label>
                        <p className="mt-1 text-sm text-gray-900 capitalize">{incident.incident_type}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Category</label>
                        <p className="mt-1 text-sm text-gray-900">{incident.category}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Subcategory</label>
                        <p className="mt-1 text-sm text-gray-900">{incident.subcategory}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description</label>
                        <p className="mt-1 text-sm text-gray-900 leading-relaxed">{incident.description}</p>
                      </div>
                      {incident.affected_groups && incident.affected_groups.length > 0 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Affected Groups</label>
                          <div className="mt-1 flex flex-wrap gap-1">
                            {incident.affected_groups.map((group: string, index: number) => (
                              <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                {group}
                              </span>
                            ))}
                          </div>
                        </div>
                      )}
                      {incident.comment1 && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Investigation Notes</label>
                          <p className="mt-1 text-sm text-gray-900 leading-relaxed bg-yellow-50 p-3 rounded border-l-4 border-yellow-400">{incident.comment1}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Reporter Information */}
                  <div className="bg-blue-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <UserIcon className="w-5 h-5 mr-2" />
                      Reporter Information
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Reporter Name</label>
                        <p className="mt-1 text-sm text-gray-900">{incident.reporter_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">User ID</label>
                        <p className="mt-1 text-sm text-gray-900">{incident.user_id}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">User Name</label>
                        <p className="mt-1 text-sm text-gray-900">{incident.user_name}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Email</label>
                        <p className="mt-1 text-sm text-gray-900">{incident.user_email}</p>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Submission Method</label>
                        <p className="mt-1 text-sm text-gray-900 capitalize">{incident.submission_method}</p>
                      </div>
                    </div>
                  </div>

                  {/* Timeline */}
                  <div className="bg-green-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <CalendarIcon className="w-5 h-5 mr-2" />
                      Timeline
                    </h3>
                    <div className="space-y-3">
                      {incident.report_date && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Incident Date</span>
                          <span className="text-sm text-gray-900">
                            {new Date(incident.report_date).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>
                      )}
                      {incident.report_time && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Incident Time</span>
                          <span className="text-sm text-gray-900">{incident.report_time}</span>
                        </div>
                      )}
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Reported to System</span>
                        <span className="text-sm text-gray-900">
                          {new Date(incident.created_at).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">Last Updated</span>
                        <span className="text-sm text-gray-900">
                          {new Date(incident.updated_at).toLocaleDateString('en-GB', {
                            year: 'numeric',
                            month: 'long',
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </span>
                      </div>
                      {incident.resolved_at && (
                        <div className="flex items-center justify-between">
                          <span className="text-sm font-medium text-gray-700">Resolved</span>
                          <span className="text-sm text-gray-900">
                            {new Date(incident.resolved_at).toLocaleDateString('en-GB', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-6">
                  {/* Location Information */}
                  <div className="bg-purple-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <MapPinIcon className="w-5 h-5 mr-2" />
                      Location Information
                    </h3>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">District</label>
                          <p className="mt-1 text-sm text-gray-900">{incident.district}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Constituency</label>
                          <p className="mt-1 text-sm text-gray-900">{incident.constituency}</p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Ward</label>
                          <p className="mt-1 text-sm text-gray-900">{incident.ward}</p>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700">Center</label>
                          <p className="mt-1 text-sm text-gray-900">{incident.center}</p>
                        </div>
                      </div>
                      {(incident.location_lat && incident.location_lng) && (
                        <div>
                          <label className="block text-sm font-medium text-gray-700">GPS Coordinates</label>
                          <p className="mt-1 text-sm text-gray-900">
                            {incident.location_lat}, {incident.location_lng}
                          </p>
                          <a
                            href={`https://www.google.com/maps?q=${incident.location_lat},${incident.location_lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center mt-2 text-sm text-blue-600 hover:text-blue-800"
                          >
                            <MapPinIcon className="w-4 h-4 mr-1" />
                            View on Google Maps
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Evidence Files */}
                  <div className="bg-yellow-50 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <DocumentIcon className="w-5 h-5 mr-2" />
                      Evidence Files ({incident.evidence_files?.length || 0})
                    </h3>
                    
                    {incident.evidence_files && incident.evidence_files.length > 0 ? (
                      <div className="space-y-4">
                        {incident.evidence_files.map((file: any, index: number) => {
                          const fileTypeInfo = getFileTypeIcon(file.mimetype);
                          const IconComponent = fileTypeInfo.icon;
                          
                          return (
                          <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center space-x-3">
                                  <div className="flex-shrink-0">
                                    <div className={`w-12 h-12 ${fileTypeInfo.bg} rounded-lg flex items-center justify-center`}>
                                      <IconComponent className={`w-6 h-6 ${fileTypeInfo.color}`} />
                                    </div>
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {file.originalname || file.filename}
                                    </p>
                                    <div className="flex items-center space-x-4 mt-1">
                                      <p className="text-xs text-gray-500">
                                        {formatFileSize(file.size)}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {file.mimetype}
                                      </p>
                                      <p className="text-xs text-gray-500">
                                        {new Date(file.upload_date).toLocaleDateString('en-GB')}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                                
                                {/* File Type Preview Thumbnail */}
                                <div className="mt-3">
                                  {isImageFile(file.mimetype) && (
                                    <div 
                                      className="h-32 bg-gray-100 border border-gray-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-gray-200 transition-colors"
                                      onClick={() => previewFileInCanvas(file)}
                                    >
                                      <div className="text-center">
                                        <EyeIcon className="w-8 h-8 text-gray-600 mx-auto mb-2" />
                                        <p className="text-sm text-gray-700 font-medium">Image File</p>
                                        <p className="text-xs text-gray-600">Click to preview</p>
                                      </div>
                                    </div>
                                  )}
                                  {isPdfFile(file.mimetype) && (
                                    <div 
                                      className="h-32 bg-red-50 border border-red-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-red-100 transition-colors"
                                      onClick={() => previewFileInCanvas(file)}
                                    >
                                      <div className="text-center">
                                        <DocumentIcon className="w-8 h-8 text-red-600 mx-auto mb-2" />
                                        <p className="text-sm text-red-700 font-medium">PDF Document</p>
                                        <p className="text-xs text-red-600">Click to preview</p>
                                      </div>
                                    </div>
                                  )}
                                  {isVideoFile(file.mimetype) && (
                                    <div 
                                      className="h-32 bg-purple-50 border border-purple-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-purple-100 transition-colors"
                                      onClick={() => previewFileInCanvas(file)}
                                    >
                                      <div className="text-center">
                                        <svg className="w-8 h-8 text-purple-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293H15M3 6V4a2 2 0 012-2h4l2 2h4a2 2 0 012 2v2M3 6h18v12a2 2 0 01-2 2H5a2 2 0 01-2-2V6z" />
                                        </svg>
                                        <p className="text-sm text-purple-700 font-medium">Video File</p>
                                        <p className="text-xs text-purple-600">Click to preview</p>
                                      </div>
                                    </div>
                                  )}
                                  {isAudioFile(file.mimetype) && (
                                    <div 
                                      className="h-32 bg-blue-50 border border-blue-200 rounded-lg flex items-center justify-center cursor-pointer hover:bg-blue-100 transition-colors"
                                      onClick={() => previewFileInCanvas(file)}
                                    >
                                      <div className="text-center">
                                        <svg className="w-8 h-8 text-blue-600 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19V6l12-3v13M9 19c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zm12-3c0 1.105-1.343 2-3 2s-3-.895-3-2 1.343-2 3-2 3 .895 3 2zM9 10l12-3" />
                                        </svg>
                                        <p className="text-sm text-blue-700 font-medium">Audio File</p>
                                        <p className="text-xs text-blue-600">Click to preview</p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                              
                              <div className="flex items-center space-x-2 ml-4">
                                <button
                                  onClick={() => previewFileInCanvas(file)}
                                  className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-xs font-medium rounded text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  <EyeIcon className="w-3 h-3 mr-1" />
                                  Preview
                                </button>
                                <button
                                  onClick={() => downloadFile(file)}
                                  className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                                >
                                  <ArrowDownTrayIcon className="w-3 h-3 mr-1" />
                                  Download
                                </button>
                              </div>
                            </div>
                          </div>
                        );
                        })}
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <DocumentIcon className="mx-auto h-12 w-12 text-gray-400" />
                        <p className="mt-2 text-sm text-gray-500">No evidence files attached to this incident</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Right Panel - File Preview */}
            <div className="w-1/2 border-l border-gray-200 bg-gray-50 flex flex-col">
              <div className="p-4 border-b border-gray-200 bg-white">
                <h3 className="text-lg font-semibold text-gray-900">File Preview</h3>
                {previewFile && (
                  <p className="text-sm text-gray-600 mt-1">{previewFile.originalname}</p>
                )}
              </div>
              
              <div className="flex-1 p-4 flex items-center justify-center">
                {previewFile ? (
                  <div className="w-full h-full flex items-center justify-center">
                    {renderFilePreview()}
                  </div>
                ) : (
                  <div className="text-center">
                    <DocumentIcon className="mx-auto h-16 w-16 text-gray-400 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No file selected</p>
                    <p className="text-gray-400 text-sm mt-2">Click on a file to preview it here</p>
                  </div>
                )}
              </div>
              
              {previewFile && (
                <div className="p-4 border-t border-gray-200 bg-white">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                      <p><strong>Size:</strong> {formatFileSize(previewFile.size)}</p>
                      <p><strong>Type:</strong> {previewFile.mimetype}</p>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => downloadFile(previewFile)}
                        className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
                      >
                        <ArrowDownTrayIcon className="w-4 h-4 mr-1" />
                        Download
                      </button>
                      <button
                        onClick={closePreview}
                        className="inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Clear
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Close
            </button>
          </div>
        </div>
      </div>

    </div>
  );
};

export default IncidentViewModal;